import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

const TileContent = ({
  children,
  className = '',
  layout = 'flex', // 'flex', 'grid', 'block', 'chart'
  direction = 'column', // 'column', 'row' (for flex layout)
  align = 'stretch', // 'start', 'center', 'end', 'stretch', 'baseline'
  justify = 'start', // 'start', 'center', 'end', 'between', 'around', 'evenly'
  gap = 'md', // 'none', 'xs', 'sm', 'md', 'lg', 'xl'
  padding = 'none', // 'none', 'sm', 'md', 'lg'
  scroll = false, // boolean or 'x', 'y', 'both'
  maxHeight = null, // string or number
  minHeight = null, // string or number
  grow = true, // whether content should grow to fill tile
  ...props
}) => {
  const contentClasses = cn(
    'w-full',
    {
      // Layout types
      'flex': layout === 'flex',
      'grid': layout === 'grid',
      'block': layout === 'block',
      'chart-container': layout === 'chart',
    },
    {
      // Flex direction (only applies to flex layout)
      'flex-col': layout === 'flex' && direction === 'column',
      'flex-row': layout === 'flex' && direction === 'row',
    },
    {
      // Flex alignment (only applies to flex layout)
      'items-start': layout === 'flex' && align === 'start',
      'items-center': layout === 'flex' && align === 'center',
      'items-end': layout === 'flex' && align === 'end',
      'items-stretch': layout === 'flex' && align === 'stretch',
      'items-baseline': layout === 'flex' && align === 'baseline',
    },
    {
      // Flex justification (only applies to flex layout)
      'justify-start': layout === 'flex' && justify === 'start',
      'justify-center': layout === 'flex' && justify === 'center',
      'justify-end': layout === 'flex' && justify === 'end',
      'justify-between': layout === 'flex' && justify === 'between',
      'justify-around': layout === 'flex' && justify === 'around',
      'justify-evenly': layout === 'flex' && justify === 'evenly',
    },
    {
      // Gap sizes
      'gap-0': gap === 'none',
      'gap-1': gap === 'xs',
      'gap-2': gap === 'sm',
      'gap-4': gap === 'md',
      'gap-6': gap === 'lg',
      'gap-8': gap === 'xl',
    },
    {
      // Padding
      'p-2': padding === 'sm',
      'p-4': padding === 'md',
      'p-6': padding === 'lg',
    },
    {
      // Scrolling
      'overflow-auto custom-scrollbar': scroll === true || scroll === 'both',
      'overflow-x-auto custom-scrollbar': scroll === 'x',
      'overflow-y-auto custom-scrollbar': scroll === 'y',
      'overflow-hidden': scroll === false,
    },
    {
      // Growth
      'flex-grow': grow && layout === 'flex',
      'h-full': grow && layout !== 'flex',
    },
    className
  );

  const styles = {
    ...(maxHeight && { maxHeight: typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight }),
    ...(minHeight && { minHeight: typeof minHeight === 'number' ? `${minHeight}px` : minHeight }),
  };

  return (
    <div 
      className={contentClasses} 
      style={styles}
      {...props}
    >
      {children}
    </div>
  );
};

// Specialized content components

// Chart Container - optimized for chart libraries
export const TileChartContent = ({
  children,
  height = 400,
  responsive = true,
  className = '',
  ...props
}) => (
  <TileContent
    layout="chart"
    className={cn(
      'relative',
      responsive ? 'aspect-auto' : '',
      className
    )}
    style={{ 
      height: typeof height === 'number' ? `${height}px` : height,
      minHeight: typeof height === 'number' ? `${height}px` : height
    }}
    {...props}
  >
    {children}
  </TileContent>
);

// Grid Content - for metric cards, data grids, etc.
export const TileGridContent = ({
  children,
  columns = 'auto-fit-200', // 'auto-fit-200', 'auto-fit-250', 'auto-fit-300', or number
  gap = 'md',
  className = '',
  ...props
}) => {
  const gridClasses = cn(
    'grid w-full',
    {
      'grid-cols-auto-fit-200': columns === 'auto-fit-200',
      'grid-cols-auto-fit-250': columns === 'auto-fit-250',
      'grid-cols-auto-fit-300': columns === 'auto-fit-300',
      'grid-cols-1': columns === 1,
      'grid-cols-2': columns === 2,
      'grid-cols-3': columns === 3,
      'grid-cols-4': columns === 4,
      'grid-cols-5': columns === 5,
      'grid-cols-6': columns === 6,
    },
    {
      'gap-1': gap === 'xs',
      'gap-2': gap === 'sm',
      'gap-4': gap === 'md',
      'gap-6': gap === 'lg',
      'gap-8': gap === 'xl',
    },
    className
  );

  return (
    <TileContent
      layout="grid"
      className={gridClasses}
      {...props}
    >
      {children}
    </TileContent>
  );
};

// Scrollable List Content
export const TileListContent = ({
  children,
  maxHeight = 400,
  gap = 'sm',
  className = '',
  ...props
}) => (
  <TileContent
    layout="flex"
    direction="column"
    gap={gap}
    scroll="y"
    maxHeight={maxHeight}
    className={cn('space-y-2', className)}
    {...props}
  >
    {children}
  </TileContent>
);

// Metric Cards Container
export const TileMetricsContent = ({
  children,
  columns = 'auto-fit-200',
  gap = 'md',
  className = '',
  ...props
}) => (
  <TileGridContent
    columns={columns}
    gap={gap}
    className={cn('items-stretch', className)}
    {...props}
  >
    {children}
  </TileGridContent>
);

// Split Content (two sections side by side)
export const TileSplitContent = ({
  left,
  right,
  leftClassName = '',
  rightClassName = '',
  ratio = '1:1', // '1:1', '2:1', '1:2', '3:1', '1:3'
  gap = 'lg',
  className = '',
  ...props
}) => {
  const ratioClasses = {
    '1:1': ['flex-1', 'flex-1'],
    '2:1': ['flex-2', 'flex-1'],
    '1:2': ['flex-1', 'flex-2'],
    '3:1': ['flex-[3]', 'flex-1'],
    '1:3': ['flex-1', 'flex-[3]'],
  };

  const [leftFlex, rightFlex] = ratioClasses[ratio] || ratioClasses['1:1'];

  return (
    <TileContent
      layout="flex"
      direction="row"
      gap={gap}
      align="stretch"
      className={cn('min-h-0', className)}
      {...props}
    >
      <div className={cn(leftFlex, 'min-w-0', leftClassName)}>
        {left}
      </div>
      <div className={cn(rightFlex, 'min-w-0', rightClassName)}>
        {right}
      </div>
    </TileContent>
  );
};

// Status Content for loading, error, empty states
export const TileStatusContent = ({
  status = 'loading', // 'loading', 'error', 'empty', 'success'
  title,
  message,
  action,
  icon,
  className = '',
  ...props
}) => {
  const { t } = useTranslation('common');

  const statusConfig = {
    loading: {
      iconComponent: (
        <div className="w-8 h-8 border-4 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
      ),
      defaultTitle: t('status.loadingTitle'),
      defaultMessage: t('status.loadingMessage'),
      bgColor: 'bg-primary-50',
      textColor: 'text-primary-700',
    },
    error: {
      iconComponent: (
        <svg className="w-8 h-8 text-error-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      defaultTitle: t('status.errorTitle'),
      defaultMessage: t('status.errorMessage'),
      bgColor: 'bg-error-50',
      textColor: 'text-error-700',
    },
    empty: {
      iconComponent: (
        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      defaultTitle: t('status.emptyTitle'),
      defaultMessage: t('status.emptyMessage'),
      bgColor: 'bg-gray-50',
      textColor: 'text-gray-600',
    },
    success: {
      iconComponent: (
        <svg className="w-8 h-8 text-success-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M5 13l4 4L19 7" />
        </svg>
      ),
      defaultTitle: t('status.successTitle'),
      defaultMessage: t('status.successMessage'),
      bgColor: 'bg-success-50',
      textColor: 'text-success-700',
    },
  };

  const config = statusConfig[status] || statusConfig.loading;

  return (
    <TileContent
      layout="flex"
      direction="column"
      align="center"
      justify="center"
      className={cn('text-center p-8', className)}
      {...props}
    >
      <div className={cn('rounded-full p-3 mb-4', config.bgColor)}>
        {icon || config.iconComponent}
      </div>
      
      {(title || config.defaultTitle) && (
        <h4 className={cn('text-lg font-medium mb-2', config.textColor)}>
          {title || config.defaultTitle}
        </h4>
      )}
      
      {(message || config.defaultMessage) && (
        <p className="text-gray-600 text-sm mb-4 max-w-sm">
          {message || config.defaultMessage}
        </p>
      )}
      
      {action && (
        <div className="mt-2">
          {action}
        </div>
      )}
    </TileContent>
  );
};

// Data Table Content
export const TileTableContent = ({
  children,
  maxHeight = 400,
  stickyHeader = true,
  className = '',
  ...props
}) => (
  <TileContent
    layout="block"
    scroll="y"
    maxHeight={maxHeight}
    className={cn(
      'overflow-auto',
      stickyHeader && '[&_thead]:sticky [&_thead]:top-0 [&_thead]:bg-white [&_thead]:z-10',
      className
    )}
    {...props}
  >
    {children}
  </TileContent>
);

export default TileContent;